import React from 'react';
import './SpellView.css';
import { damageLinesColorStyle, getSpellLineIcon } from '../../core/weapon';
import {
    calcLineExpectedVal,
    dedupeIdenticalAlternatives,
    filterSpellLinesForDisplay,
    isAllyOnlyDamageLine,
    isPercentMaxHpHealLine,
    formatSpellLineConditionText,
    maskXorBaseKey,
    primaryAlternativeComponents,
    spellHasPrimaryBaseDamage,
    spellHasChargeStacking,
    spellHasMpPeakBonus,
    sortRowsByConditionDisplayOrder,
} from '../../core/spellUtils';
import MediaImg from '../utils/MediaImg';
import ChargesPicker from '../utils/ChargesPicker';
import { isPickableScaling, maxCount } from '../../core/spellCharges';
import CriterionWeight from '../generator/CriterionWeight';
import { spellLineCriterionSignature } from '../../core/criteriaWeights';

const isHealLine = (line) => line?.kind === 'heal';

const isClientOnlyChargeBasePrimary = (line) =>
    line?.kind === 'damage'
    && line?.clientOnly === true
    && Number.isFinite(line?.stackIndex)
    && line.stackIndex === 0
    && Number.isFinite(line?.stackMax)
    && line.stackMax >= 1;

const isClientOnlyTimedPrimary = (line) =>
    line?.kind === 'damage'
    && line?.clientOnly === true
    && (line?.triggers === 'TB' || line?.triggers === 'TE' || Number(line?.delay) > 0);

const isClientOnlyPrimaryDamage = (line) =>
    isClientOnlyChargeBasePrimary(line) || isClientOnlyTimedPrimary(line);

const getCondText = (line, blockLabel, spell) => formatSpellLineConditionText({
    line,
    blockLabel,
    lang: spell?.lang,
    healLabel: spell?.healLabel,
});

const includeInPrimaryDmgRow = (line, suppressAllyOnly = false) => {
    if (isHealLine(line)) return false;
    // Element-variant lines are mutually exclusive alternatives. Keep them out
    // of the merged primary icon-strip row so each element renders as its own
    // row — same per-line treatment the details view uses.
    if (line?.elVariantXorKey) return false;
    if (suppressAllyOnly && isAllyOnlyDamageLine(line)) return false;
    if (line.kind === 'base_damage_bonus') return false;
    if (line.clientOnly && !isClientOnlyPrimaryDamage(line)) return false;
    if (line.clientOnlyProxy) return false;
    if (line.summonOnlyDmg || line.mpBonusDmg) return false;
    if (line.isEmbedded) return true;
    const si = line.stackIndex;
    if (si != null && Number.isFinite(si) && si !== 0) return false;
    return true;
};

const shouldSuppressAllyOnlyPrimaryForSpell = (spell) => {
    const hasEnemyPrimary = [...(spell?.baseLines || []), ...(spell?.conditionalBlocks || []).flatMap((b) => b.lines || [])]
        .some((line) =>
            line?.kind === 'damage'
            && line?.affectsEnemies === true
            && includeInPrimaryDmgRow(line, false)
        );
    return hasEnemyPrimary;
};

const isSameTickPrimaryLine = (line) =>
    (line?.triggers == null || line.triggers === 'I')
    && (!Number.isFinite(Number(line?.delay)) || Number(line.delay) === 0);

const mapBonus = (spell) =>
    (spell.bonusLines || []).map((line, idx) => ({
        key: `${spell.id}-bdb-${idx}`,
        line,
        condText: line.bonusLabel,
        val: calcLineExpectedVal(line, spell.critPct ?? 0),
    }));

const mapImmediate = (spell) =>
    spell.baseLines.map((line, idx) => ({
        key: `${spell.id}-base-${line.el}-${idx}`,
        line,
        val: calcLineExpectedVal(line, spell.critPct ?? 0),
    }));

/** Stacked 293 tiers (stack_index ≥ 1): extra damage rows on the compact card. */
const mapStackedBaseRows = (immediate, spell) =>
    {
        const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
        return immediate
        .filter(({ line }) => !isHealLine(line) && !includeInPrimaryDmgRow(line, suppressAllyOnly))
        .map(({ key, line, val }) => ({
            key,
            line,
            condText: getCondText(line, null, spell),
            val,
        }));
    };

const mapConditional = (spell, { hidePrimaryPromoted = false, promotedCondKeys = [] } = {}) => {
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
    const out = [];
    spell.conditionalBlocks.forEach((block, bi) => {
        block.lines.forEach((line, idx) => {
            if (
                hidePrimaryPromoted
                && promotedCondKeys.includes(block.condKey)
                && line.kind === 'damage'
                && includeInPrimaryDmgRow(line, suppressAllyOnly)
            ) return;
            out.push({
                key: `${spell.id}-cond-${bi}-${line.el}-${idx}`,
                condKey: block.condKey,
                line,
                condText: getCondText(line, block.label, spell),
                val: calcLineExpectedVal(line, spell.critPct ?? 0),
            });
        });
    });
    return out;
};

/** Split portal blocks into dmg vs heal rows (Eliotrope primary merge). */
const flattenPortalBlocks = (spell) => {
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
    const dmg = [];
    const heals = [];
    spell.conditionalBlocks
        .filter((b) => b.condKey === 'through_portal')
        .forEach((block, bi) => {
            block.lines.forEach((line, idx) => {
                const key = `${spell.id}-tp-${bi}-${line.el}-${idx}`;
                const val = calcLineExpectedVal(line, spell.critPct ?? 0);
                if (isHealLine(line)) {
                    heals.push({
                        key,
                        line,
                        val,
                        condText: getCondText(line, block.label, spell),
                    });
                } else if (includeInPrimaryDmgRow(line, suppressAllyOnly) && isSameTickPrimaryLine(line)) {
                    dmg.push({
                        key,
                        condKey: block.condKey,
                        line,
                        val,
                    });
                }
            });
        });
    return { dmg, heals };
};

/**
 * The "sans portail" cast as one row: everything ungated, plus the ``direct`` lines.
 *
 * An Eliotrope card headlines the portal cast — base lines plus the ``through_portal``
 * block — which leaves a ``direct``-gated line with nowhere to go. Affront's push
 * disappeared from the card entirely that way: unlike Sermon's, it has no portal twin to
 * be pulled in beside. Both casts are a row now, and each carries every line that lands
 * in it, so the icons say what you actually get.
 *
 * Only for spells whose damage is ungated. When it is not, the two gates are branches of
 * one hit rather than two casts, and splitPrimaryVariants already pairs them (Audace,
 * Faisceau).
 */
const portalDirectCastRow = (spell, baseDmgRows) => {
    if (!spellHasPrimaryBaseDamage(spell)) return null;
    const block = (spell.conditionalBlocks || []).find((b) => b.condKey === 'direct');
    if (!block) return null;
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
    const directLines = (block.lines || []).filter(
        (line) => line.kind === 'damage'
            && includeInPrimaryDmgRow(line, suppressAllyOnly)
            && isSameTickPrimaryLine(line),
    );
    if (!directLines.length) return null;
    const critPct = spell.critPct ?? 0;
    const lines = [...(baseDmgRows || []).map(({ line }) => line), ...directLines];
    const seen = new Set();
    const iconLines = lines.filter((line) => {
        const sig = `${line?.kind || ''}:${line?.el || ''}`;
        if (seen.has(sig)) return false;
        seen.add(sig);
        return true;
    });
    return {
        key: `${spell.id}-portal-direct-cast`,
        condKey: 'direct',
        line: directLines[0],
        iconLines,
        condText: block.label || getCondText(directLines[0], null, spell),
        val: lines.reduce((sum, line) => sum + calcLineExpectedVal(line, critPct), 0),
    };
};


/** Conditional rows, optionally skipping condKeys already merged into Eliotrope primary. */
const mapConditionalExcluding = (
    spell,
    excludeCondKeys = [],
    { hidePrimaryPromoted = false, promotedCondKeys = [] } = {},
) => {
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
    const out = [];
    spell.conditionalBlocks.forEach((block, bi) => {
        const excludedBlock = excludeCondKeys.includes(block.condKey);
        if (excludedBlock && block.condKey !== 'through_portal') return;
        block.lines.forEach((line, idx) => {
            if (excludedBlock && isSameTickPrimaryLine(line)) return;
            if (
                hidePrimaryPromoted
                && promotedCondKeys.includes(block.condKey)
                && line.kind === 'damage'
                && includeInPrimaryDmgRow(line, suppressAllyOnly)
            ) return;
            out.push({
                key: `${spell.id}-cond-${bi}-${line.el}-${idx}`,
                condKey: block.condKey,
                line,
                condText: getCondText(line, block.label, spell),
                val: calcLineExpectedVal(line, spell.critPct ?? 0),
            });
        });
    });
    return out;
};

const primaryVariantGroupKey = (row) => {
    const line = row?.line;
    if (line?.kind !== 'damage' || line?.el === 'push') return null;
    if (row?.condKey === 'through_portal') return null;
    if (line?.exclusiveGroupKey) return `xor:${line.exclusiveGroupKey}`;
    const effectId = line?.effectId;
    if (effectId != null && effectId !== '') {
        return `effect:${effectId}:${line.el}:${line.isLifesteal ? 1 : 0}`;
    }
    return null;
};

/** A cast goes through a portal or it does not — never both. */
const PORTAL_GATE_COND_KEYS = ['through_portal', 'direct'];

const primaryVariantFamilyKey = (row) => {
    const line = row?.line;
    if (!line || line.kind !== 'damage' || line.el === 'push') return null;
    const effectId = line?.effectId;
    if (effectId == null || effectId === '') return null;
    const trig = line?.triggers ?? 'I';
    const delay = Number.isFinite(Number(line?.delay)) ? Number(line.delay) : 0;
    const si = Number.isFinite(Number(line?.stackIndex)) ? Number(line.stackIndex) : 'x';
    const sm = Number.isFinite(Number(line?.stackMax)) ? Number(line.stackMax) : 'x';
    return [
        effectId,
        line.el,
        line.isLifesteal ? 1 : 0,
        trig,
        delay,
        si,
        sm,
    ].join('|');
};

const isChargeBasePrimaryRow = (row) =>
    row?.line?.kind === 'damage'
    && Number.isFinite(row.line?.stackIndex)
    && row.line.stackIndex === 0
    && Number.isFinite(row.line?.stackMax)
    && row.line.stackMax >= 1;

/** Same effect/element primary rows are target/branch variants, not additive header hits. */
const splitPrimaryVariants = (primaryDmg, lang = 'fr') => {
    const rowLabel = (row) => (
        row?.condText
        || formatSpellLineConditionText({
            line: row?.line,
            lang,
            fallback: 'direct',
        })
        || ''
    ).trim();
    const rowNormLabel = (row) => rowLabel(row).toLowerCase();
    const familyRows = new Map();
    (primaryDmg || []).forEach((row) => {
        const fk = primaryVariantFamilyKey(row);
        if (!fk) return;
        if (!familyRows.has(fk)) familyRows.set(fk, []);
        familyRows.get(fk).push(row);
    });
    const forcedFamilyGroupByRowKey = new Map();
    const forcedFamilyGroupKeys = new Set();
    familyRows.forEach((rows, fk) => {
        if (!rows || rows.length < 2) return;
        const labels = Array.from(new Set(rows.map((r) => rowNormLabel(r)).filter(Boolean)));
        // Same damage profile but different condition labels => variant buckets, not additive header rows.
        if (labels.length < 2) return;
        forcedFamilyGroupKeys.add(`family:${fk}`);
        rows.forEach((row) => {
            if (row?.key) forcedFamilyGroupByRowKey.set(row.key, `family:${fk}`);
        });
    });
    // A cast is either through a portal or not, so the two gates are exclusive even
    // when the branches carry different effect ids or lifesteal (Faisceau: 99 vs 94),
    // which is what the damage-profile family key above cannot see.
    const portalGateRows = new Map();
    (primaryDmg || []).forEach((row) => {
        if (row?.key && forcedFamilyGroupByRowKey.has(row.key)) return;
        if (!PORTAL_GATE_COND_KEYS.includes(row?.condKey)) return;
        const line = row?.line;
        if (line?.kind !== 'damage' || line?.el === 'push') return;
        const gk = `portal:${line.el}|${line.triggers ?? 'I'}|${Number(line.delay) || 0}`;
        if (!portalGateRows.has(gk)) portalGateRows.set(gk, []);
        portalGateRows.get(gk).push(row);
    });
    portalGateRows.forEach((rows, gk) => {
        if (!rows || rows.length < 2) return;
        if (new Set(rows.map((r) => r.condKey)).size < 2) return;
        forcedFamilyGroupKeys.add(gk);
        rows.forEach((row) => {
            if (row?.key) forcedFamilyGroupByRowKey.set(row.key, gk);
        });
    });
    const effectiveVariantGroupKey = (row) =>
        (row?.key ? forcedFamilyGroupByRowKey.get(row.key) : null)
        || primaryVariantGroupKey(row);

    const groups = new Map();
    const firstByGroup = new Map();
    (primaryDmg || []).forEach((row) => {
        const g = effectiveVariantGroupKey(row);
        if (!g) return;
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g).push(row);
        if (!firstByGroup.has(g) || isChargeBasePrimaryRow(row)) {
            firstByGroup.set(g, row);
            return;
        }
        if (forcedFamilyGroupKeys.has(g)) {
            const cur = firstByGroup.get(g);
            if ((Number(row?.val) || 0) > (Number(cur?.val) || 0)) {
                firstByGroup.set(g, row);
            }
        }
    });
    const primaryRows = [];
    const secondaryRows = [];
    (primaryDmg || []).forEach((row, idx) => {
        const g = effectiveVariantGroupKey(row);
        if (!g || !groups.has(g) || groups.get(g).length < 2) {
            primaryRows.push(row);
            return;
        }
        if (firstByGroup.get(g) === row) {
            primaryRows.push(row);
            return;
        }
        const condText = row.condText
            || formatSpellLineConditionText({
                line: row.line,
                lang,
                fallback: 'direct',
            });
        secondaryRows.push({
            key: `${row.key}-primary-variant-${idx}`,
            line: row.line,
            condText,
            val: row.val,
            iconLines: row.iconLines,
        });
    });
    return { primaryRows, secondaryRows };
};

/**
 * A variant row that repeats the primary hit verbatim is noise on the card.
 *
 * Resquille showed "Direct 266" under its own 266 header: the same water hit, split
 * only by which target category Dofus wrote it for. Rows that differ — a gated branch,
 * a raised charge tier — are kept, and so is a hit the spell genuinely lands twice.
 */
const dropSecondaryRowsSayingNothingNew = ({ primaryRows, secondaryRows }) => {
    if (!secondaryRows?.length) return secondaryRows || [];
    const primaryLines = (primaryRows || []).map(({ line }) => line).filter(Boolean);
    const kept = new Set(dedupeIdenticalAlternatives([
        ...primaryLines,
        ...secondaryRows.map(({ line }) => line),
    ]));
    return secondaryRows.filter(({ line }) => !line || kept.has(line));
};

/** Non-winning XOR primary branches should still be visible as secondary outcomes. */
const mapPrimaryExclusiveAlternatives = (primaryDmg) => {
    const groups = new Map();
    (primaryDmg || []).forEach((row) => {
        const g = row?.line?.exclusiveGroupKey;
        if (!g || row?.line?.kind !== 'damage' || row?.line?.el === 'push') return;
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g).push(row);
    });
    const out = [];
    groups.forEach((rows, gk) => {
        if (!rows || rows.length < 2) return;
        rows.slice(1).forEach((row, idx) => {
            out.push({
                key: `${row.key}-xor-alt-${gk}-${idx}`,
                line: row.line,
                condText: getCondText(row.line, null, null),
                val: row.val,
            });
        });
    });
    return out;
};

const targetUnderStateBranchKey = (row) => {
    const line = row?.line;
    const specs = (line?.conditions || [])
        .filter((c) => c?.id === 'target_under_state')
        .map((c) => {
            const sid = c?.state_id ?? c?.stateId;
            if (sid == null || sid === '') return null;
            const neg = c?.state_negated ?? c?.stateNegated;
            const tok = c?.state_token_case ?? c?.stateTokenCase ?? '';
            return `${sid}:${neg === true ? 1 : 0}:${tok}`;
        })
        .filter(Boolean);
    if (specs.length) return specs.join('|');
    const fallback = String(
        row?.condText
        || line?.targetLabel
        || line?.condition
        || line?.stackRowCaption
        || line?.targetMask
        || ''
    ).trim();
    if (!fallback) return null;
    return `label:${fallback.toLowerCase()}`;
};

const dedupeBranchIconLines = (lines) => {
    const seen = new Set();
    const out = [];
    for (const ln of lines || []) {
        const sig = `${ln?.kind || ''}:${ln?.el || ''}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        out.push(ln);
    }
    return out;
};

const rowIconLines = (row) => (
    Array.isArray(row?.iconLines) && row.iconLines.length
        ? row.iconLines
        : [row?.line].filter(Boolean)
);

const collapseCasterBestStatePrimaryRows = (primaryRows, spell) => {
    if (!['caster_best_element', 'target_under_state'].includes(spell?.damageCondition)) return null;
    const groups = new Map();
    const order = [];
    let hasDamage = false;
    for (const row of primaryRows || []) {
        if (row?.line?.kind !== 'damage' || row?.line?.el === 'push') continue;
        hasDamage = true;
        const key = targetUnderStateBranchKey(row);
        if (!key) {
            continue;
        }
        if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
        }
        groups.get(key).push(row);
    }
    if (!hasDamage || groups.size < 2) return null;
    const out = [];
    for (const key of order) {
        const rows = groups.get(key) || [];
        if (!rows.length) continue;
        const first = rows[0];
        const iconLines = spell?.damageCondition === 'target_under_state'
            ? rows.flatMap(rowIconLines)
            : dedupeBranchIconLines(rows.flatMap(rowIconLines));
        const sourceKeys = Array.from(new Set(rows.flatMap((r) => r?.line?.sourceKeys || [])));
        const line = { ...first.line, sourceKeys };
        const condText = first.condText || getCondText(first.line, null, spell);
        out.push({
            key: `${first.key}-state-branch`,
            line,
            condText,
            val: rows.reduce((sum, r) => sum + (Number(r?.val) || 0), 0),
            iconLines,
        });
    }
    if (out.length < 2) return null;
    return out.sort((a, b) => (Number(b?.val) || 0) - (Number(a?.val) || 0));
};

const applyStatePrimaryCollapse = (primaryDmg, spell) => {
    const collapsedStateBranches = collapseCasterBestStatePrimaryRows(primaryDmg, spell);
    if (!collapsedStateBranches || collapsedStateBranches.length === 0) {
        return { primaryDmg: null, secondaryRows: [], forcedPrimaryIconLines: null };
    }
    const [bestBranch, ...altBranches] = collapsedStateBranches;
    return {
        primaryDmg: [{
            key: `${bestBranch.key}-primary`,
            line: bestBranch.line,
            val: bestBranch.val,
            condText: bestBranch.condText,
            iconLines: bestBranch.iconLines,
        }],
        secondaryRows: altBranches.map((row, idx) => ({
            ...row,
            key: `${row.key}-secondary-${idx}`,
        })),
        forcedPrimaryIconLines: bestBranch.iconLines || null,
    };
};

const ALCH_XOR_ELS = new Set(['earth', 'fire', 'water', 'air']);

const rangeSig = (r) => {
    if (!r) return 'x:x';
    const mn = Number.isFinite(Number(r?.min)) ? Number(r.min) : 'x';
    const mx = Number.isFinite(Number(r?.max)) ? Number(r.max) : 'x';
    return `${mn}:${mx}`;
};

const alchVariantSig = (line) => {
    const trig = line?.triggers ?? 'I';
    const delay = Number.isFinite(Number(line?.delay)) ? Number(line.delay) : 0;
    const si = Number.isFinite(Number(line?.stackIndex)) ? Number(line.stackIndex) : 'x';
    const sm = Number.isFinite(Number(line?.stackMax)) ? Number(line.stackMax) : 'x';
    return `${trig}|${delay}|${si}|${sm}|${rangeSig(line?.baseRaw || line?.base)}|${rangeSig(line?.critRaw || line?.crit)}`;
};

const markAlchemicalVariantXor = (spell, primaryRows, immediateRows) => {
    if (!Array.isArray(primaryRows) || primaryRows.length < 4) return primaryRows;
    if (!Array.isArray(immediateRows) || immediateRows.length < 5) return primaryRows;
    const dmgGroups = new Map();
    const healGroups = new Map();
    const addGroup = (map, key, el) => {
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(el);
    };
    for (const row of immediateRows) {
        const line = row?.line;
        if (!line || !ALCH_XOR_ELS.has(line.el)) continue;
        const sig = alchVariantSig(line);
        if (line.kind === 'damage') addGroup(dmgGroups, sig, line.el);
        if (line.kind === 'heal') addGroup(healGroups, sig, line.el);
    }
    const hasFullSet = (set) => set && ['earth', 'fire', 'water', 'air'].every((el) => set.has(el));
    const hasAnyHeal = Array.from(healGroups.values()).some((set) => set && set.size > 0);
    const candidateSigs = Array.from(dmgGroups.keys()).filter((sig) =>
        hasFullSet(dmgGroups.get(sig)) && (hasFullSet(healGroups.get(sig)) || hasAnyHeal)
    );
    if (!candidateSigs.length) return primaryRows;
    const out = primaryRows.map((row) => ({ ...row, line: { ...(row?.line || {}) } }));
    candidateSigs.forEach((sig, i) => {
        const xorKey = `alch_variant_xor:${spell?.id ?? 'x'}:${i}`;
        out.forEach((row) => {
            const line = row?.line;
            if (!line || line.kind !== 'damage' || !ALCH_XOR_ELS.has(line.el)) return;
            if (line.el === 'neutral') return;
            if (alchVariantSig(line) !== sig) return;
            line.exclusiveGroupKey = line.exclusiveGroupKey || xorKey;
        });
    });
    return out;
};

const mergeTierRows = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) return rows || [];
    const out = [];
    const byKey = new Map();
    const condStartsWithCaption = (condText, caption) => {
        const cond = String(condText || '').trim().toLowerCase();
        const cap = String(caption || '').trim().toLowerCase();
        if (!cond || !cap) return false;
        return cond === cap || cond.startsWith(`${cap} ·`) || cond.startsWith(`${cap} •`) || cond.startsWith(`${cap}:`);
    };
    const isMergeable = (row) => (
        row?.line?.kind === 'damage'
        && row?.line?.el !== 'push'
        && Number.isFinite(row?.line?.stackIndex)
        && row.line.stackIndex >= 1
        && Number.isFinite(row?.line?.stackMax)
        && row.line.stackMax >= 1
        && !row?.line?.exclusiveGroupKey
        && !!row?.condText
        && condStartsWithCaption(row.condText, row?.line?.stackRowCaption)
    );
    const addIcon = (dst, src) => {
        if (!src) return;
        const sig = `${src.kind || ''}:${src.el || ''}`;
        if (dst._seen.has(sig)) return;
        dst._seen.add(sig);
        dst.iconLines.push(src);
    };
    rows.forEach((row) => {
        if (!isMergeable(row)) {
            out.push(row);
            return;
        }
        const k = `${row.condText}|${row.line.stackIndex}|${row.line.stackMax}|${row.line.targetMask || ''}`;
        const grp = byKey.get(k);
        if (!grp) {
            const next = {
                key: `${row.key}-merged`,
                line: row.line,
                condText: row.condText,
                val: row.val || 0,
                iconLines: [],
                sourceKeys: [...(row.line?.sourceKeys || [])],
                emphasizeOnSelect: true,
                rowClass: 'spell-view-row-charge-merged',
                _seen: new Set(),
            };
            (row.iconLines || [row.line]).forEach((ln) => addIcon(next, ln));
            byKey.set(k, next);
            out.push(next);
            return;
        }
        grp.val += row.val || 0;
        grp.sourceKeys = Array.from(new Set([...(grp.sourceKeys || []), ...(row.line?.sourceKeys || [])]));
        grp.line = { ...grp.line, sourceKeys: grp.sourceKeys };
        (row.iconLines || [row.line]).forEach((ln) => addIcon(grp, ln));
    });
    return out.map((row) => {
        if (!row?._seen) return row;
        const { _seen, ...rest } = row;
        return rest;
    });
};

const cardRowConditionSig = (line) => JSON.stringify(
    (line?.conditions || []).map((c) => ({
        id: c?.id ?? null,
        stateId: c?.state_id ?? c?.stateId ?? null,
        neg: c?.state_negated ?? c?.stateNegated ?? null,
        tok: c?.state_token_case ?? c?.stateTokenCase ?? null,
    })),
);

const cardRowMergeKey = (row) => {
    const line = row?.line;
    if (!line) return null;
    const delay = Number.isFinite(Number(line?.delay)) ? Number(line.delay) : 0;
    const stackIndex = Number.isFinite(Number(line?.stackIndex)) ? Number(line.stackIndex) : 'x';
    const stackMax = Number.isFinite(Number(line?.stackMax)) ? Number(line.stackMax) : 'x';
    const randomChance = Number.isFinite(Number(line?.randomChance)) ? Number(line.randomChance) : 'x';
    const randomGroup = Number.isFinite(Number(line?.randomGroup)) ? Number(line.randomGroup) : 'x';
    const normMask = maskXorBaseKey(line?.targetMask) ?? line?.targetMask ?? '';
    const baseMin = line?.base?.min ?? 'x';
    const baseMax = line?.base?.max ?? 'x';
    const critMin = line?.crit?.min ?? 'x';
    const critMax = line?.crit?.max ?? 'x';
    return [
        row?.condKey || '',
        String(row?.condText || '').trim().toLowerCase(),
        line?.kind || '',
        line?.el || '',
        line?.effectId ?? '',
        normMask,
        line?.triggers ?? 'I',
        delay,
        stackIndex,
        stackMax,
        line?.exclusiveGroupKey || '',
        randomChance,
        randomGroup,
        cardRowConditionSig(line),
        baseMin,
        baseMax,
        critMin,
        critMax,
    ].join('|');
};

const collapseDuplicateCardRows = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) return rows || [];
    const out = [];
    const byKey = new Map();
    rows.forEach((row) => {
        const key = cardRowMergeKey(row);
        if (!key) {
            out.push(row);
            return;
        }
        const existing = byKey.get(key);
        const currentHitCount = Number(row?.line?.cardHitCount);
        const hitCount = Number.isFinite(currentHitCount) && currentHitCount >= 1 ? currentHitCount : 1;
        if (!existing) {
            const sourceKeys = Array.from(new Set(row?.line?.sourceKeys || []));
            const iconLines = Array.isArray(row?.iconLines) && row.iconLines.length
                ? [...row.iconLines]
                : [row.line];
            const seed = {
                ...row,
                line: { ...(row?.line || {}), sourceKeys, cardHitCount: hitCount },
                iconLines,
            };
            byKey.set(key, seed);
            out.push(seed);
            return;
        }
        existing.val = (Number(existing.val) || 0) + (Number(row.val) || 0);
        const sourceKeys = Array.from(new Set([
            ...(existing?.line?.sourceKeys || []),
            ...(row?.line?.sourceKeys || []),
        ]));
        existing.line = {
            ...(existing?.line || {}),
            sourceKeys,
            cardHitCount: (Number(existing?.line?.cardHitCount) || 1) + hitCount,
        };
        const extraIconLines = Array.isArray(row?.iconLines) && row.iconLines.length
            ? row.iconLines
            : [row.line];
        existing.iconLines = [...(existing.iconLines || []), ...extraIconLines];
    });
    return out;
};

const aggregateRandomRowsByMask = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) return rows || [];
    const keyOf = (row) => {
        const rnd = Number(row?.line?.randomChance);
        if (!Number.isFinite(rnd) || rnd <= 0) return null;
        return [
            row?.line?.kind || '',
            row?.line?.targetMask || '',
            row?.condText || '',
        ].join('|');
    };
    const grouped = new Map();
    rows.forEach((row, idx) => {
        const k = keyOf(row);
        if (!k) return;
        if (!grouped.has(k)) grouped.set(k, { firstIndex: idx, rows: [] });
        grouped.get(k).rows.push(row);
    });
    const emitted = new Set();
    const out = [];
    const addIcon = (dst, src) => {
        if (!src) return;
        const sig = `${src.kind || ''}:${src.el || ''}`;
        if (dst._seen.has(sig)) return;
        dst._seen.add(sig);
        dst.iconLines.push(src);
    };
    rows.forEach((row) => {
        const k = keyOf(row);
        if (!k) {
            out.push(row);
            return;
        }
        if (emitted.has(k)) return;
        emitted.add(k);
        const grp = grouped.get(k);
        if (!grp || grp.rows.length < 2) {
            out.push(row);
            return;
        }
        let wSum = 0;
        let weighted = 0;
        const merged = {
            ...row,
            key: `${row.key}-rand-agg`,
            iconLines: [],
            sourceKeys: [],
            _seen: new Set(),
        };
        for (const r of grp.rows) {
            const w = Number(r?.line?.randomChance);
            if (Number.isFinite(w) && w > 0) {
                wSum += w;
                weighted += w * (Number(r?.val) || 0);
            }
            merged.sourceKeys = Array.from(new Set([...(merged.sourceKeys || []), ...(r?.line?.sourceKeys || [])]));
            (r.iconLines || [r.line]).forEach((ln) => addIcon(merged, ln));
        }
        merged.line = { ...merged.line, sourceKeys: merged.sourceKeys };
        if (wSum > 0) merged.val = Math.round(weighted / wSum);
        out.push(merged);
    });
    return out.map((row) => {
        if (!row?._seen) return row;
        const { _seen, ...rest } = row;
        return rest;
    });
};

const resolvePromotedConditionalKeys = (spell, eliotropePortalCardOnly) => {
    if (spellHasPrimaryBaseDamage(spell)) return [];
    const timedCondKeys = new Set(['start_of_turn', 'end_of_turn']);
    const hasTimedBlock = (spell.conditionalBlocks || []).some((block) => timedCondKeys.has(block.condKey));
    if (hasTimedBlock) {
        return (spell.conditionalBlocks || [])
            .map((block) => block.condKey)
            .filter((key) => timedCondKeys.has(key));
    }
    return (spell.conditionalBlocks || [])
        .map((block) => block.condKey)
        .filter((key) => !(eliotropePortalCardOnly && key === 'through_portal'));
};

/** Conditional damage promoted to card header when there is no qualifying base damage (e.g. Barrière, Morsure du Serpent). */
const conditionalPromotedPrimaryRows = (spell, eliotropePortalCardOnly) => {
    if (spellHasPrimaryBaseDamage(spell)) return [];
    const critPct = spell.critPct ?? 0;
    const promotedCondKeys = resolvePromotedConditionalKeys(spell, eliotropePortalCardOnly);
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);

    const out = [];
    spell.conditionalBlocks.forEach((block, bi) => {
        if (!promotedCondKeys.includes(block.condKey)) return;

        block.lines.forEach((line, idx) => {
            if (line.kind !== 'damage' || !includeInPrimaryDmgRow(line, suppressAllyOnly)) return;
            out.push({
                key: `${spell.id}-cond-primary-${bi}-${line.el}-${idx}`,
                condKey: block.condKey,
                line,
                condText: getCondText(line, block.label, spell),
                val: calcLineExpectedVal(line, critPct),
            });
        });
    });

    return out;
};

const collectPrimaryIconLines = (row, eliotropePortalCardOnly) => {
    const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(row);
    const basePrimary = (row.baseLines || []).filter(
        (line) => line.kind === 'damage' && includeInPrimaryDmgRow(line, suppressAllyOnly),
    );
    const conditionalPrimary = [];
    if (!spellHasPrimaryBaseDamage(row)) {
        (row.conditionalBlocks || []).forEach((block) => {
            if (eliotropePortalCardOnly && block.condKey === 'through_portal') return;
            (block.lines || []).forEach((line) => {
                if (line.kind === 'damage' && includeInPrimaryDmgRow(line, suppressAllyOnly)) conditionalPrimary.push(line);
            });
        });
    }
    const out = [];
    const seen = new Set();
    for (const ln of [...basePrimary, ...conditionalPrimary]) {
        const sig = `${ln.kind}:${ln.el}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        out.push(ln);
    }
    return out;
};

const SpellView = ({
    spell: rawSpell,
    labels,
    eliotropePortalCardOnly = false,
    selected = false,
    selectedLineKeys = new Set(),
    onToggleLine = null,
    weights = null,
    selectable = true,
    onIconActivate = null,
    iconActivateLabel = '',
    // `{ lineKey: n }` for this spell's charged lines read below their maximum.
    lineCharges = null,
    onChargesChange = null,
}) => {
    const spell = filterSpellLinesForDisplay(rawSpell);
    let primaryDmg;
    let immediateHealRows;
    let conditionalRows;
    let forcedPrimaryIconLines = null;

    const hidePrimaryPromoted = !spellHasPrimaryBaseDamage(spell);
    const promotedCondKeys = hidePrimaryPromoted
        ? resolvePromotedConditionalKeys(spell, eliotropePortalCardOnly)
        : [];

    if (eliotropePortalCardOnly) {
        const immediate = mapImmediate(spell);
        const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
        const baseDmg = immediate.filter(({ line }) => !isHealLine(line) && includeInPrimaryDmgRow(line, suppressAllyOnly));
        const baseHealRows = immediate
            .filter(({ line }) => isHealLine(line))
            .map(({ key, line, val }) => ({
                key,
                line,
                val,
                condText: getCondText(line, null, spell),
            }));
        const portal = flattenPortalBlocks(spell);
        primaryDmg = [
            ...baseDmg,
            ...portal.dmg,
            ...conditionalPromotedPrimaryRows(spell, true),
        ];
        primaryDmg = markAlchemicalVariantXor(spell, primaryDmg, immediate);
        primaryDmg = collapseDuplicateCardRows(primaryDmg);
        const stateCollapse = applyStatePrimaryCollapse(primaryDmg, spell);
        let primarySplit;
        if (stateCollapse.primaryDmg) {
            primaryDmg = stateCollapse.primaryDmg;
            forcedPrimaryIconLines = stateCollapse.forcedPrimaryIconLines;
            primarySplit = { primaryRows: primaryDmg, secondaryRows: stateCollapse.secondaryRows };
        } else {
            primarySplit = splitPrimaryVariants(primaryDmg, spell?.lang || 'fr');
            primaryDmg = primarySplit.primaryRows;
            primarySplit = {
                ...primarySplit,
                secondaryRows: dropSecondaryRowsSayingNothingNew(primarySplit),
            };
        }
        const xorAltRows = mapPrimaryExclusiveAlternatives(primarySplit.primaryRows);
        immediateHealRows = [...baseHealRows, ...portal.heals];
        // Hide portal/direct split branches on card; primary already encodes “portal” build (+ universal).
        const directCastRow = portalDirectCastRow(spell, baseDmg);
        conditionalRows = [
            ...mapStackedBaseRows(immediate, spell),
            ...primarySplit.secondaryRows,
            ...(directCastRow ? [directCastRow] : []),
            ...mapConditionalExcluding(
                spell,
                ['through_portal', 'direct'],
                { hidePrimaryPromoted, promotedCondKeys },
            ),
            ...xorAltRows,
            ...mapBonus(spell),
        ];
    } else {
        const immediate = mapImmediate(spell);
        const suppressAllyOnly = shouldSuppressAllyOnlyPrimaryForSpell(spell);
        primaryDmg = [
            ...immediate.filter(({ line }) => !isHealLine(line) && includeInPrimaryDmgRow(line, suppressAllyOnly)),
            ...conditionalPromotedPrimaryRows(spell, false),
        ];
        primaryDmg = markAlchemicalVariantXor(spell, primaryDmg, immediate);
        primaryDmg = collapseDuplicateCardRows(primaryDmg);
        const stateCollapse = applyStatePrimaryCollapse(primaryDmg, spell);
        let primarySplit;
        if (stateCollapse.primaryDmg) {
            primaryDmg = stateCollapse.primaryDmg;
            forcedPrimaryIconLines = stateCollapse.forcedPrimaryIconLines;
            primarySplit = { primaryRows: primaryDmg, secondaryRows: stateCollapse.secondaryRows };
        } else {
            primarySplit = splitPrimaryVariants(primaryDmg, spell?.lang || 'fr');
            primaryDmg = primarySplit.primaryRows;
            primarySplit = {
                ...primarySplit,
                secondaryRows: dropSecondaryRowsSayingNothingNew(primarySplit),
            };
        }
        const xorAltRows = mapPrimaryExclusiveAlternatives(primarySplit.primaryRows);
        immediateHealRows = immediate
            .filter(({ line }) => isHealLine(line))
            .map(({ key, line, val }) => ({
                key,
                line,
                val,
                condText: getCondText(line, null, spell),
            }));
        conditionalRows = [
            ...mapStackedBaseRows(immediate, spell),
            ...primarySplit.secondaryRows,
            ...mapConditional(spell, { hidePrimaryPromoted, promotedCondKeys }),
            ...xorAltRows,
            ...mapBonus(spell),
        ];
    }
    immediateHealRows = collapseDuplicateCardRows(immediateHealRows);
    conditionalRows = collapseDuplicateCardRows(conditionalRows);
    immediateHealRows = aggregateRandomRowsByMask(immediateHealRows);
    conditionalRows = aggregateRandomRowsByMask(conditionalRows);

    const elIcon = (line) => getSpellLineIcon(line);

    const peakRowStyle = spellHasChargeStacking(spell) || spellHasMpPeakBonus(spell);
    const cardTotal = spell.cardPrimaryTotal;
    const headerTotal = spell.cardPrimaryHeaderTotal ?? spell.cardPrimaryTotal;
    let displayCardTotal = cardTotal;
    let displayHeaderTotal = headerTotal;
    let emphSecondaryKey = null;
    const isStatePushBranch = (row) =>
        row?.line?.kind === 'damage'
        && row?.line?.el === 'push'
        && row?.condKey === 'target_under_state';

    const basePrimaryDamageRows = (primaryDmg || []).filter(
        ({ line }) => line?.kind === 'damage' && line?.el !== 'push',
    );
    const conditionalDamageRows = (conditionalRows || []).filter(({ line }) => line?.kind === 'damage');
    const statePushRows = conditionalDamageRows.filter((row) => isStatePushBranch(row));
    const nonStatePushDamageRows = conditionalDamageRows.filter((row) => !isStatePushBranch(row));
    // If all conditional damage branches are state-gated push rows, show branch outcomes only.
    const showBranchRowsOnly = basePrimaryDamageRows.length > 0
        && statePushRows.length > 0
        && nonStatePushDamageRows.length === 0;

    const dedupeIconLines = (lines) => {
        const seen = new Set();
        const out = [];
        for (const ln of lines || []) {
            const sig = `${ln?.kind || ''}:${ln?.el || ''}`;
            if (seen.has(sig)) continue;
            seen.add(sig);
            out.push(ln);
        }
        return out;
    };
    const baseBranchIconLines = dedupeIconLines(basePrimaryDamageRows.map(({ line }) => line));
    const conditionalRowsDisplay = (conditionalRows || []).map((row) => {
        if (!isStatePushBranch(row)) return row;
        return {
            ...row,
            val: (headerTotal || 0) + (row.val || 0),
            iconLines: dedupeIconLines([...baseBranchIconLines, row.line]),
        };
    });
    let primaryDmgDisplay = showBranchRowsOnly ? [] : primaryDmg;
    let conditionalRowsRendered = conditionalRowsDisplay;
    let primaryIconLinesOverride = forcedPrimaryIconLines;
    if (showBranchRowsOnly && statePushRows.length > 0) {
        let best = null;
        for (const row of conditionalRowsDisplay) {
            if (!isStatePushBranch(row)) continue;
            if (!best || (row.val || 0) > (best.val || 0)) best = row;
        }
        if (best) {
            displayCardTotal = best.val || 0;
            displayHeaderTotal = displayCardTotal;
            primaryDmgDisplay = [{ key: `${best.key}-primary`, line: best.line }];
            primaryIconLinesOverride = best.iconLines || dedupeIconLines([...baseBranchIconLines, best.line]);
            conditionalRowsRendered = conditionalRowsDisplay.filter((row) => row.key !== best.key);
        }
    }
    const findStackPair = () => {
        const chargedPrimary = (primaryDmgDisplay || []).find(({ line }) =>
            line?.kind === 'damage'
            && Number.isFinite(line?.stackIndex)
            && line.stackIndex >= 1
            && Number.isFinite(line?.stackMax)
            && line.stackMax >= 1,
        );
        if (!chargedPrimary) return null;
        const baseIdx = (conditionalRowsRendered || []).findIndex((row) =>
            row?.line?.kind === 'damage'
            && Number.isFinite(row.line?.stackIndex)
            && row.line.stackIndex === 0
            && Number.isFinite(row.line?.stackMax)
            && row.line.stackMax >= 1
            && row.line?.effectId === chargedPrimary.line?.effectId
            && row.line?.el === chargedPrimary.line?.el,
        );
        if (baseIdx < 0) return null;
        return { chargedPrimary, baseIdx };
    };
    const stackPair = findStackPair();
    if (stackPair) {
        const { chargedPrimary, baseIdx } = stackPair;
        const baseRow = conditionalRowsRendered[baseIdx];
        displayHeaderTotal = baseRow?.val ?? displayHeaderTotal;
        primaryDmgDisplay = [{ key: `${baseRow.key}-primary`, line: baseRow.line, val: baseRow.val }];
        primaryIconLinesOverride = [baseRow.line];
        conditionalRowsRendered = [
            ...conditionalRowsRendered.slice(0, baseIdx),
            ...conditionalRowsRendered.slice(baseIdx + 1),
            {
                key: `${chargedPrimary.key}-secondary`,
                line: chargedPrimary.line,
                condText: getCondText(chargedPrimary.line, null, spell),
                val: chargedPrimary.val,
            },
        ];
    }
    conditionalRowsRendered = sortRowsByConditionDisplayOrder(
        mergeTierRows(conditionalRowsRendered),
    );

    const headerEmph = displayHeaderTotal === displayCardTotal || !!stackPair;
    /**
     * One icon per hit, using the grouping the header total already uses — an
     * exclusive_branch_key alone missed that Oblitération's enemy row is the same
     * hit as its bomb row, so the card drew two earth icons over a single cast.
     */
    const primaryDmgForIcons = (() => {
        const rows = (primaryDmgDisplay || []).filter(({ line }) => line);
        // Group across every primary line the spell owns, not just the rows in this
        // strip: the link that proves two of them are one hit can be a line the card
        // shows somewhere else. Oblitération's enemy row and its charge-base row look
        // unrelated on their own — they are tied together through the "Conditionnel"
        // row, which sits among the conditional rows further down.
        const spellPrimaryLines = (spell.baseLines || [])
            .filter((line) => line.kind === 'damage' && line.el !== 'push');
        const componentOf = new Map();
        primaryAlternativeComponents([...rows.map(({ line }) => line), ...spellPrimaryLines])
            .forEach((comp, idx) => comp.lines.forEach((line) => componentOf.set(line, idx)));
        const seen = new Set();
        return rows.filter(({ line }) => {
            const comp = componentOf.get(line);
            if (comp == null) return true;
            if (seen.has(comp)) return false;
            seen.add(comp);
            return true;
        });
    })();
    if (!primaryIconLinesOverride && primaryDmgDisplay.length === 1) {
        // Row merging concatenates the icon lines of everything it folded together, so
        // one hit written two ways arrives as two icons. Group them the way the total
        // does. (The state-branch override above is deliberately left alone: Cerbère
        // really lands three times, and its three icons say so.)
        const mergedIconLines = primaryDmgDisplay[0]?.iconLines;
        if (Array.isArray(mergedIconLines) && mergedIconLines.length > 0) {
            primaryIconLinesOverride = primaryAlternativeComponents(mergedIconLines)
                .map((comp) => comp.lines[0]);
        }
    }

    if (peakRowStyle && conditionalRowsRendered.length && !stackPair) {
        let bestSi = -Infinity;
        for (const row of conditionalRowsRendered) {
            if (row.val !== displayCardTotal) continue;
            const l = row.line;
            if (l.kind === 'heal') continue;
            let si = -Infinity;
            if (l.kind === 'damage' && l.stackIndex != null && Number.isFinite(l.stackIndex)) {
                si = l.stackIndex;
            } else if (l.kind === 'base_damage_bonus') {
                si = -1;
            }
            if (si >= bestSi) {
                bestSi = si;
                emphSecondaryKey = row.key;
            }
        }
    }
    const summonSpawnRows = (() => {
        if (!spell.summonChildRows?.length) return [];
        return spell.summonChildRows.map((cRow) => {
            const iconLines = collectPrimaryIconLines(cRow, eliotropePortalCardOnly);
            const fallbackLine = iconLines[0]
                || (cRow.baseLines || []).find((line) => line.kind === 'damage')
                || (cRow.baseLines || []).find((line) => line.kind === 'heal')
                || { kind: 'damage', el: 'neutral' };

            let total = Number(cRow.cardPrimaryTotal) || 0;
            if (total === 0 && fallbackLine.kind === 'heal') {
                total = Math.floor((fallbackLine.base?.min + fallbackLine.base?.max) / 2) || fallbackLine.base?.max || 0;
            }

            return {
                key: `${spell.id}-sum-${cRow.id}`,
                line: fallbackLine,
                iconLines: iconLines.length ? iconLines : [fallbackLine],
                val: total,
                condText: cRow.name,
            };
        });
    })();

    const hasSummonChildren = Array.isArray(spell.summonChildRows) && spell.summonChildRows.length > 0;
    const portraitSrc = hasSummonChildren
        ? (spell.summonMonsterIconUrl || spell.icon)
        : spell.icon;

    const baseSpellId = (() => {
        const n = Number(String(spell?.id ?? '').split('_')[0]);
        return Number.isFinite(n) ? n : null;
    })();
    const keysFromLines = (lines) => Array.from(new Set(
        (lines || [])
            .flatMap((line) => line?.sourceKeys || [])
            .map((x) => String(x || '').trim())
            .filter(Boolean)
    )).sort();
    const criterionFromLines = (lines) => {
        if (baseSpellId == null) return null;
        if ((lines || []).some(isPercentMaxHpHealLine)) return null;
        const lineKeys = keysFromLines(lines);
        if (!lineKeys.length) return null;
        return { spell_id: baseSpellId, line_keys: lineKeys };
    };
    const isCriterionSelected = (criterion) => {
        const sig = spellLineCriterionSignature(criterion);
        return !!sig && !!selectedLineKeys?.has?.(sig);
    };
    const toggleCriterion = (criterion) => {
        if (!criterion || !onToggleLine || !selectable) return;
        onToggleLine(criterion);
    };
    const selectableRowProps = (criterion) => {
        if (!criterion || !onToggleLine || !selectable) return {};
        return {
            role: 'button',
            tabIndex: 0,
            'aria-pressed': isCriterionSelected(criterion),
            onClick: (e) => {
                e.preventDefault();
                toggleCriterion(criterion);
            },
            onKeyDown: (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                toggleCriterion(criterion);
            },
        };
    };
    const isRowSelectable = (criterion) => !!criterion && !!onToggleLine && !!selectable;
    // Weights column: every row gets the cell so the value columns stay aligned
    // down the card, but only a selected line carries a weight to put in it.
    const renderWeight = (criterion) => {
        if (!weights) return null;
        const selectedHere = !!criterion && isCriterionSelected(criterion);
        return (
            <div className="spell-view-weight-cell">
                {selectedHere ? (
                    <CriterionWeight
                        value={weights.valueOf(criterion)}
                        onChange={(next) => weights.onChange(criterion, next)}
                        disabled={weights.disabled || !selectable}
                        lang={weights.lang}
                    />
                ) : null}
            </div>
        );
    };
    // What the icon strip draws, and so what the total beside it is dealt in.
    const primaryIconLines = primaryIconLinesOverride
        || primaryDmgForIcons.map(({ line }) => line);
    const primaryCriterion = criterionFromLines(
        primaryIconLinesOverride || (primaryDmgDisplay || []).map(({ line }) => line),
    );
    const primarySelected = isCriterionSelected(primaryCriterion);
    const MAX_ROWS = 3;
    const [secondaryExpanded, setSecondaryExpanded] = React.useState(false);
    const allSecondary = [
        ...conditionalRowsRendered.map((r) => ({ ...r, _isCond: true })),
        ...immediateHealRows.map((r) => ({ ...r, _isCond: false })),
    ];
    const hasSecondaryOverflow = allSecondary.length > MAX_ROWS;
    const hasHiddenActiveSecondary = React.useMemo(() => {
        if (!hasSecondaryOverflow) return false;
        const hiddenRows = allSecondary.slice(MAX_ROWS);
        return hiddenRows.some((row) => {
            const criterion = criterionFromLines([row?.line, ...((row?.iconLines) || [])]);
            return isCriterionSelected(criterion);
        });
        // criterionFromLines/isCriterionSelected are per-render closures over
        // selectedLineKeys, which is already tracked; depending on them directly
        // would defeat the memo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSecondary, hasSecondaryOverflow, selectedLineKeys, spell?.id]);
    React.useEffect(() => {
        if (!hasHiddenActiveSecondary) return;
        setSecondaryExpanded(true);
    }, [hasHiddenActiveSecondary]);
    const shownSecondary = secondaryExpanded ? allSecondary : allSecondary.slice(0, MAX_ROWS);
    const toggleSecondaryExpanded = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setSecondaryExpanded((v) => !v);
    };

    /**
     * The peak row of a charged hit ("5 charges", "4 lancers"): its caption
     * becomes a picker for 1..max, and the rest of its condition stays beside it. The
     * row's numbers already reflect the pick (calcSpellRows, core/spellCharges).
     */
    const isChargedPeak = (line) => isPickableScaling(line?.scalingKind)
        && Number.isFinite(line?.stackIndex)
        && line.stackIndex >= 1
        && maxCount(line.scalingKind, line.stackIndex) >= 2
        && line.stackIndex === line?.stackMax
        && line?.embeddedFromSpellId == null
        && line?.embeddingCarrierEffectId == null;
    const renderCondition = (line, condText) => {
        if (!isChargedPeak(line) || typeof onChargesChange !== 'function') {
            return condText ? <span className="spell-view-cond">{condText}</span> : null;
        }
        const keys = line.sourceKeys || [];
        const max = maxCount(line.scalingKind, line.stackMax);
        const picked = keys.map((k) => lineCharges?.[k]).find((n) => n != null);
        const value = Math.min(max, Math.max(1, picked ?? max));
        const caption = String(line.stackRowCaption || '');
        const text = String(condText || '');
        const rest = caption && text.startsWith(caption)
            ? text.slice(caption.length).replace(/^\s*[·•:]\s*/, '')
            : text;
        return (
            <span className="spell-view-cond spell-view-cond--charges">
                <ChargesPicker
                    value={value}
                    max={max}
                    kind={line.scalingKind}
                    lang={spell?.lang || 'fr'}
                    disabled={!selectable}
                    onChange={(n) => onChargesChange(keys, n, max)}
                />
                {rest && rest !== caption ? <span>{rest}</span> : null}
            </span>
        );
    };

    const renderSecondaryRow = ({
        key,
        line,
        condText,
        val,
        rowClass = '',
        valClass = '',
        iconLines = null,
        criterion = null,
    }) => {
        const rowSelected = isCriterionSelected(criterion);
        return (
            <div
                key={key}
                className={`spell-view-row spell-view-row-extra ${rowClass}${isRowSelectable(criterion) ? ' spell-view-row--selectable' : ''}${rowSelected ? ' spell-view-row--selected' : ''}`.trim()}
                {...selectableRowProps(criterion)}
            >
                {renderCondition(line, condText) || <span className="spell-view-cond-placeholder" />}
                <div className="spell-view-val-cell">
                    <span
                        className={`spell-view-val ${valClass}`.trim()}
                        style={damageLinesColorStyle(iconLines || [line])}
                    >
                        {val}
                    </span>
                    <div className="spell-view-el-icons">
                        {(iconLines || [line]).map((iconLine, i) => {
                            const src = elIcon(iconLine);
                            if (src) {
                                return <img key={`${key}-icon-${i}`} src={src} alt="" className="spell-view-el-icon" aria-hidden="true" />;
                            }
                            return (
                                <span key={`${key}-icon-${i}`} className="spell-view-el-fallback">
                                    {iconLine?.el === 'heal_total' ? spell.healLabel : (labels[iconLine?.el] || iconLine?.el)}
                                </span>
                            );
                        })}
                    </div>
                </div>
                {renderWeight(criterion)}
            </div>
        );
    };

    // The icon is the tap target for the details overlay on pointers that can't
    // hover; where hover works `onIconActivate` is null and it stays inert.
    const renderIcon = () => {
        const img = portraitSrc ? (
            <MediaImg className="spell-view-icon" src={portraitSrc} alt={spell.name} loading="lazy" />
        ) : (
            <div className="spell-view-icon spell-view-icon-fallback" aria-hidden="true" />
        );
        if (!onIconActivate) return img;
        return (
            <span
                className="spell-view-icon-trigger"
                role="button"
                tabIndex={0}
                aria-label={iconActivateLabel || spell.name}
                onClick={(e) => {
                    e.stopPropagation();
                    onIconActivate();
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    onIconActivate();
                }}
            >
                {img}
            </span>
        );
    };

    return (
        <div className={`spell-view${selectable ? '' : ' spell-view--readonly'}`}>
            <div className="spell-view-compact-icon">
                {renderIcon()}
                {hasSecondaryOverflow && (
                    <span
                        className="spell-view-more-label spell-view-more-label--column spell-view-more-label--toggle"
                        role="button"
                        tabIndex={0}
                        aria-expanded={secondaryExpanded}
                        onClick={toggleSecondaryExpanded}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            toggleSecondaryExpanded(e);
                        }}
                    >
                        {secondaryExpanded ? '− moins' : `+${allSecondary.length - MAX_ROWS} ...`}
                    </span>
                )}
            </div>
            <div className="spell-view-compact-rows">
                <div
                    className={`spell-view-row spell-view-row-first${isRowSelectable(primaryCriterion) ? ' spell-view-row--selectable' : ''}${primarySelected ? ' spell-view-row--selected' : ''}`.trim()}
                    {...selectableRowProps(primaryCriterion)}
                >
                    <div className="spell-view-name">
                        {spell.name}
                    </div>
                    {primaryDmgDisplay.length > 0 && (
                        <div className="spell-view-val-cell">
                            <span
                                className={`spell-view-val ${headerEmph ? 'spell-view-val--emphasis' : ''}`.trim()}
                                style={damageLinesColorStyle(primaryIconLines)}
                            >
                                {displayHeaderTotal}
                            </span>
                            <div className="spell-view-el-icons" aria-hidden="true">
                                {primaryIconLines.map((line, i) => {
                                    const src = elIcon(line);
                                    return src ? (
                                        <img key={`pd-icon-${i}`} src={src} alt="" className="spell-view-el-icon" />
                                    ) : (
                                        <span key={`pd-icon-${i}`} className="spell-view-el-fallback">
                                            {labels[line.el] || line.el || '?'}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {renderWeight(primaryCriterion)}
                </div>
                {shownSecondary.map(({ _isCond, key, line, condText, val, iconLines, rowClass, emphasizeOnSelect }) => {
                    const criterion = criterionFromLines([line, ...(iconLines || [])]);
                    if (_isCond) {
                        const isSelectedEmphasis = selected && (emphSecondaryKey === key || emphasizeOnSelect);
                        return renderSecondaryRow({
                            key,
                            line,
                            condText: condText || undefined,
                            val,
                            iconLines,
                            rowClass: `${rowClass || 'spell-view-row-secondary'}${isSelectedEmphasis ? ' spell-view-row-secondary-emphasis' : ''}`,
                            valClass: isSelectedEmphasis ? 'spell-view-val--emphasis' : '',
                            criterion,
                        });
                    }
                    return renderSecondaryRow({ key, line, condText, val, iconLines, rowClass: 'spell-view-row-secondary', criterion });
                })}
                {summonSpawnRows.map((row) => renderSecondaryRow({
                    ...row,
                    rowClass: 'spell-view-row-secondary spell-view-row-summon-spawn',
                    valClass: selected ? 'spell-view-val--emphasis' : '',
                    criterion: criterionFromLines([row.line, ...(row.iconLines || [])]),
                }))}
            </div>
        </div>
    );
};

export default SpellView;
